/**
 * Utility functions for copy formatters
 */

/**
 * Add a section separator
 */
export const sectionSeparator = (char: string = '='): string => {
  return char.repeat(50);
};

/**
 * Format a heading with separator
 */
export const formatHeading = (text: string, level: number = 1): string => {
  const separator = level === 1 ? '=' : level === 2 ? '-' : '•';
  return `\n${separator.repeat(3)} ${text.toUpperCase()} ${separator.repeat(3)}\n`;
};

/**
 * Format a bullet list
 */
export const formatBulletList = (items: string[], bullet: string = '•'): string => {
  if (!items || items.length === 0) return '';
  return items.map((item) => `${bullet} ${item}`).join('\n');
};

/**
 * Format key-value pairs
 */
export const formatKeyValue = (key: string, value: string | number): string => {
  return `${key}: ${value}`;
};

/**
 * Add line breaks between sections
 */
export const addSpacing = (text: string, lines: number = 2): string => {
  return text + '\n'.repeat(lines);
};

/**
 * Remove undefined/null values and join with newlines
 */
export const joinSections = (...sections: (string | undefined | null)[]): string => {
  return sections.filter(Boolean).join('\n\n');
};

/**
 * Ensure text preserves line breaks
 */
export const preserveLineBreaks = (text: string): string => {
  return text || '';
};

/**
 * Format a markdown table
 * @param headers - Array of header strings
 * @param rows - Array of row arrays
 * @param alignments - Optional alignment for each column ('left', 'center', 'right')
 */
export function formatTable(
  headers: string[],
  rows: string[][],
  alignments?: ('left' | 'center' | 'right')[],
): string {
  if (!headers || headers.length === 0 || !rows || rows.length === 0) return '';

  const lines: string[] = [];

  // Header row
  lines.push(`| ${headers.join(' | ')} |`);

  // Separator row with alignment
  const separators = headers.map((_, index) => {
    const align = alignments?.[index] || 'left';
    if (align === 'center') return ':---:';
    if (align === 'right') return '---:';
    return '---';
  });
  lines.push(`| ${separators.join(' | ')} |`);

  // Data rows
  rows.forEach((row) => {
    lines.push(`| ${row.join(' | ')} |`);
  });

  return lines.join('\n');
}
